import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from './Erasmith.module.css';
import techVideo from '../../../images/erasmith.mp4';

const ErasmithProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [hoveredService, setHoveredService] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Service Architecture & Content Strategy', icon: '💡' },
    { text: 'UX & Information Hierarchy Design', icon: '🎯' },
    { text: 'Technology Narrative Integration', icon: '⚙️' },
    { text: 'Website Content Design & Deployment', icon: '🌐' },
    { text: 'Enterprise Value Messaging', icon: '📊' },
    { text: 'Brand & Visual Alignment', icon: '🎨' },
    { text: 'Continuous Optimization', icon: '🚀' }
  ];

  const tools = [
    { name: 'WordPress / CMS', icon: '📝', description: 'Content Management' },
    { name: 'Figma', icon: '🎨', description: 'UX Flows & Wireframes' },
    { name: 'Photoshop', icon: '🖼️', description: 'Visual Assets' },
    { name: 'Illustrator', icon: '✨', description: 'Icons & Diagrams' },
    { name: 'Analytics Tools', icon: '📊', description: 'Performance Tracking' }
  ];

  const skills = [
    'HTML5', 'CSS', 'Tailwind CSS', 'JavaScript', 'UX Architecture',
    'Content Strategy', 'Enterprise Solutions', 'Digital Transformation',
    'Brand Positioning', 'SEO Optimization'
  ];

  

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Technology Services Platform</h1>
                <p className={styles.projectSubtitle}>Digital Transformation & Technology Consulting</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Strategy, Content Architecture, UX Structuring</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2020 – Present</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  A Technology Services Platform focused on delivering end-to-end digital transformation solutions across AI, cloud applications, ERP integration, data systems, and enterprise platforms. It enables businesses to modernize operations, streamline workflows, and scale efficiently through intelligent, future-ready digital ecosystems.
                </p>
                
                
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={techVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                This platform empowers organizations to evolve by combining technology, strategy, and design thinking into cohesive digital solutions. It supports a wide range of services including enterprise software development, cloud migration, AI-driven platforms, ERP implementations, and business process optimization. With a strong emphasis on scalability, security, and performance, the platform is designed to help businesses build resilient systems and achieve long-term value through digital innovation.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                My role involved shaping the platform’s digital presence and service communication by translating complex technical offerings into clear, structured, and business-focused content. I worked on improving content architecture, refining user flow, and aligning messaging with enterprise needs. The focus was on making advanced technology solutions more accessible while strengthening clarity, usability, and overall brand positioning across digital touchpoints.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>

          
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default ErasmithProjectShowcase;